import { OrganizationDepartmentRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiSheet, useAppForm, zodOptionalPhoneNumberSchema } from 'noddi-ui-web';
import { useEffect } from 'react';
import { z } from 'zod';
import { invalidateDepartmentQueries } from './invalidateDepartmentQueries';

const FORM_ID = 'edit-department-contact-details-form';

const validationSchema = z.object({
  email: z.email('Invalid email').or(z.literal('')),
  phoneNumber: zodOptionalPhoneNumberSchema,
  website: z.url('Invalid URL').or(z.literal(''))
});

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  department: OrganizationDepartmentRecord;
  departmentId: number;
};

export const EditDepartmentContactDetailsSheet = ({ open, onClose, department, departmentId }: Props) => {
  const { mutateAsync: patchDepartment } = noddiAsync.usePatch({
    type: URLKeys.patchDepartment,
    queryConfig: {
      onSuccess: () => {
        invalidateDepartmentQueries(departmentId);
        onClose();
      }
    }
  });

  const getDefaultValues = (): FormValues => ({
    email: department.contactDetails?.email ?? '',
    phoneNumber: department.contactDetails?.phoneNumber ?? '',
    website: department.contactDetails?.website ?? ''
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(),
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      await patchDepartment({
        departmentId,
        contactDetails: {
          email: value.email || null,
          // Parse to apply the schema's whitespace-trim — TanStack Form never
          // writes transform output back into form state.
          phoneNumber: zodOptionalPhoneNumberSchema.parse(value.phoneNumber) || null,
          website: value.website || null
        }
      });
    }
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getDefaultValues is a new closure each render and would reset on every render; reset only on open or when the fields it reads change
  }, [
    open,
    form,
    department.contactDetails?.email,
    department.contactDetails?.phoneNumber,
    department.contactDetails?.website
  ]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit contact details'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save' className='w-full' />}
      >
        <form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-5'>
            <form.AppField name='email'>{(field) => <field.Input label='Email' type='email' />}</form.AppField>
            <form.AppField name='phoneNumber'>{(field) => <field.Input label='Phone number' />}</form.AppField>
            <form.AppField name='website'>{(field) => <field.Input label='Website' type='url' />}</form.AppField>
          </div>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
